/** How to play: a short page on the main menu (docs/ui-decisions.md, "How
 *  to play"). The basics only, a few lines a topic; keys shown as the
 *  player has them bound. Then the structures, each with its map glyph,
 *  and how goods flow between them. */

import { RECIPE_TABS, tabOf } from "../state/recipes";
import { goodSvg, recipeIconGood } from "../ui/goodIcons";
import { For, Show, type JSX } from "solid-js";
import { Shape, SpriteFlag, glyph, type GlyphSpec } from "../render/glyphs";
import * as keys from "../ui/bindings";

/** Each structure: its glyph, name, steel (construction.rs: its work over
 *  WORK_PER_STEEL), and what it does (recipes from goods.rs, per line and
 *  run). Track has no glyph: it's drawn as a line. */
const STRUCTURES: { g: GlyphSpec | null; name: string; steel: string; does: string }[] = [
  { g: { shape: Shape.Home, size: 1.2, fill: 0.7 }, name: "Home", steel: "", does: "Where you start: holds rounds and freight, signs up recruits from your people, and makes 10 steel a minute while you hold under 30. Lose every depot and you start again at a new home." },
  { g: { shape: Shape.Depot, fill: 0.6 }, name: "Depot", steel: "100", does: "Holds rounds. Your units nearby take ammunition from it." },
  { g: { shape: Shape.Depot, fill: 0.6, flags: SpriteFlag.FIELD, size: 0.75 }, name: "Field depot", steel: "", does: "Opened by your supply lines near the front on their own, to carry rounds forward." },
  { g: { shape: Shape.Station }, name: "Station", steel: "150", does: "A stop on your track: trains load and unload freight here. Holds up to 50,000 of each good (home 1,500); a full station stops what makes the good." },
  { g: null, name: "Track", steel: "5 steel a hex", does: "Joins stations; trains run on it. It bridges narrow water. Built from both ends at once, it goes twice as fast. Free until your steel production is running." },
  { g: { shape: Shape.MineWorks }, name: "Mine", steel: "200", does: "Digs every ore within two hexes: a double deposit yields twice. It's a station itself: join it to your track." },
  { g: { shape: Shape.PlantWorks }, name: "Steelworks", steel: "300", does: "2 iron ore and 1 coal make 1 steel: 144 steel a minute from 288 iron ore and 144 coal, about one iron mine's output." },
  { g: { shape: Shape.PlantWorks }, name: "Munitions plant", steel: "300", does: "1 steel makes 20 rounds." },
  { g: { shape: Shape.PlantWorks }, name: "Recruiting office", steel: "300", does: "Signs up recruits from your people (no goods in)." },
  { g: { shape: Shape.PlantWorks }, name: "Locomotive works", steel: "300", does: "100 steel makes a train, about one a minute, for its station's fleet." },
  { g: { shape: Shape.PlantWorks }, name: "Copper smelter", steel: "300", does: "3 copper ore and 1 coal make 1 copper." },
  { g: { shape: Shape.PlantWorks }, name: "Refinery", steel: "300", does: "2 oil make 1 fuel." },
  { g: { shape: Shape.PlantWorks }, name: "Mint", steel: "300", does: "Strikes copper into credits at its worth. Credits trade without track, and across the storm wall." },
  { g: { shape: Shape.Producer }, name: "Barracks", steel: "150", does: "Trains units, paying steel and recruits from its own station." },
  { g: { shape: Shape.Post }, name: "Guard post", steel: "300", does: "Automated defence (after Automated defences): holds its hex and fires on anyone who enters your ground or shoots at it. Never moves." },
  { g: null, name: "Storm breaker", steel: "2,000", does: "Built against the storm wall (after Storm breaching): breaks it open 24 hexes round, and the storm re-forms beyond. Spent when it's done." },
];

/** Reading the map: each kind of line or wash, a small drawn sample (SVG,
 *  in the colours the map uses) and what it means. */
const LINES: [JSX.Element, string][] = [
  [<line x1="2" y1="8" x2="46" y2="8" stroke="rgba(124,214,124,0.9)" stroke-width="2" stroke-dasharray="6 4" />, "A move order being drawn, and the route your selected units will take (green)."],
  [<line x1="2" y1="8" x2="46" y2="8" stroke="rgba(239,91,91,0.95)" stroke-width="2" stroke-dasharray="6 4" />, "A fight order: they attack what they meet on the way (red)."],
  [<line x1="2" y1="8" x2="46" y2="8" stroke="rgba(111,183,255,0.8)" stroke-width="1.5" stroke-dasharray="4 4" />, "A patrol, repeating (blue)."],
  [<line x1="2" y1="8" x2="46" y2="8" stroke="rgba(242,210,122,0.8)" stroke-width="1.5" stroke-dasharray="3 3" />, "Where a barracks sends its new units (amber)."],
  [
    <>
      <line x1="2" y1="8" x2="46" y2="8" stroke="#4d3d2e" stroke-width="6" />
      <line x1="2" y1="8" x2="46" y2="8" stroke="#9e8061" stroke-width="2" />
    </>,
    "Rail. Trains on it: your colour carrying troops, rust freight, grey rounds.",
  ],
  [<rect x="2" y="3" width="44" height="10" fill="rgba(191,173,115,0.45)" />, "A supply corridor (Layers, Corridors): convoys' road from depots to the front."],
  [
    <>
      <line x1="2" y1="8" x2="40" y2="8" stroke="#d0603a" stroke-width="3" />
      <polygon points="46,8 36,3 36,13" fill="#d0603a" />
    </>,
    "Goods on their way between stations, a good each in its colour, thicker the more.",
  ],
  [<line x1="2" y1="8" x2="46" y2="8" stroke="var(--accent)" stroke-width="1.5" stroke-dasharray="2 3" />, "An engineer at work: a beam to what it's building."],
  [<line x1="2" y1="8" x2="46" y2="8" stroke="#f2efe8" stroke-width="1" stroke-dasharray="3 4" opacity="0.6" />, "An engineer on its way to a site."],
  [<rect x="2" y="2" width="44" height="12" fill="var(--accent)" opacity="0.3" />, "Settled ground, washed in its settler's colour: deeper is more settled."],
  [
    <>
      <ellipse cx="16" cy="8" rx="13" ry="6" fill="#d0603a" opacity="0.7" />
      <ellipse cx="34" cy="8" rx="12" ry="6" fill="#3cc8a8" opacity="0.6" />
    </>,
    "Ore found: each ore in its colour, brighter where richer (Layers, Ore found).",
  ],
];

/** The server's recipes as last seen in a game (How to Play has no state
 *  of its own): each plant's inputs and outputs, [good, name, n]. */
interface Recipe {
  name: string;
  inputs: [number, string, number][];
  outputs: [number, string, number][];
}
const readRecipes = (): Recipe[] => {
  try {
    const l = JSON.parse(localStorage.getItem("ferro.recipes") ?? "[]");
    return Array.isArray(l) ? l : [];
  } catch {
    return [];
  }
};

/** Plants by the recipe picker's tabs, so the page and the game group
 *  them the same. */
const familyOf = (name: string) => tabOf(name);
const amount = (l: [number, string, number][]) => l.map(([, g, n]) => `${n.toLocaleString("en-US")} ${g.toLowerCase()}`).join(" and ");

/** The plants: the written ones as they are, the rest from their recipes
 *  ("2 bauxite and 1 coal make 1 aluminium"); every plant in a family. */
const plantRows = (recipes: Recipe[]) => {
  const written = new Map(STRUCTURES.filter((x) => x.g?.shape === Shape.PlantWorks).map((x) => [x.name, x]));
  const names = recipes.length ? recipes.map((r) => r.name) : [...written.keys()];
  return names.map((name) => {
    const w = written.get(name);
    const r = recipes.find((x) => x.name === name);
    const does = w?.does ?? (r && r.outputs.length ? (r.inputs.length ? `${amount(r.inputs)} make ${amount(r.outputs)}.` : `Makes ${amount(r.outputs)}.`) : "");
    return { g: { shape: Shape.PlantWorks } as GlyphSpec, icon: r ? recipeIconGood(r) : null, name, steel: w?.steel ?? "300", does, family: familyOf(name) };
  });
};
const familiesIn = (rows: { family: string }[]) => [...RECIPE_TABS.map((t) => t.name), "Other"].filter((f) => rows.some((p) => p.family === f));
/** The structures that aren't plants (the plants are listed by family). */
const OTHER_STRUCTURES = STRUCTURES.filter((x) => x.g?.shape !== Shape.PlantWorks);

/** How goods flow, as chains of boxes: each a step and what it makes. */
const chainsOf = (recipes: Recipe[]): [string, string][][] => [
  [["Mine", "iron ore, coal"], ["Steelworks", "steel"], ["Munitions plant", "rounds"], ["Depot", "ammunition"], ["Your units", ""]],
  [["Home", "people"], ["Recruiting office", "recruits"], ["Barracks", "+ steel"], ["New units", ""]],
  [["Steel", ""], ["Everything you build", ""]],
  // The rest from the recipes: what each plant takes and makes.
  ...recipes
    .filter((r) => r.inputs.length && r.outputs.length && !["Steelworks", "Munitions plant", "Recruiting office"].includes(r.name))
    .map((r): [string, string][] => [
      [r.inputs.length > 1 ? "Mines and plants" : "Mine", r.inputs.map(([, g]) => g.toLowerCase()).join(", ")],
      [r.name, r.outputs.map(([, g]) => g.toLowerCase()).join(", ")],
    ]),
];

const k = (id: string) => <kbd>{keys.hint(id)}</kbd>;

interface Topic {
  title: string;
  lines: JSX.Element[];
}

export function HowToPlay(): JSX.Element {
  // The recipes as last seen in a game, read as the page opens.
  const recipes = readRecipes();
  const rows = plantRows(recipes);
  const families = familiesIn(rows);
  const chains = chainsOf(recipes);
  const topics: Topic[] = [
    {
      title: "Getting around",
      lines: [
        <>Arrow keys or drag with the middle button to pan; the wheel zooms.</>,
        <>{k("overview")} shows the whole map, and again takes you back.</>,
        <>Click the minimap to look somewhere.</>,
      ],
    },
    {
      title: "Commanding",
      lines: [
        <>Drag a box to select your units; click one to select it alone.</>,
        <>Right-click to move. Drag with the right button to spread them along a line.</>,
        <>
          {k("fight")} then click to attack-move, {k("patrol")} to patrol, {k("stop")} to stop. Hold Shift to queue orders.
        </>,
        <>Ctrl and a number keeps a group; the number selects it again.</>,
        <>Units keep the peace: without a Fight order they only return fire, or fire on anyone who comes onto your ground.</>,
      ],
    },
    {
      title: "Building",
      lines: [
        <>Open the Build tab, choose a structure and click where it goes. Your engineers go and build it themselves.</>,
        <>Track takes two clicks: where it starts and where it ends, then carries on from there. It can bridge narrow water.</>,
        <>Drag while placing to lay a row; Shift+drag lays a grid over the box. Right-click cancels a drag.</>,
        <>Sites show as ghosts on the map, filling as they're built.</>,
      ],
    },
    {
      title: "Your economy",
      lines: [
        <>Your prospectors survey around home on their own; where they find ore, the ground glows, brighter the richer.</>,
        <>On the move they survey at once (roughly how much ore). Stopped, they sample: soil says which ores (2 minutes), a deep core exactly how much (20 minutes).</>,
        <>The top bar shows your steel, rounds, recruits and people, and what comes in and goes out each minute.</>,
        <>Mines dig ore. A steelworks makes steel from iron ore and coal; a munitions plant makes rounds from steel.</>,
        <>Your home turns people into recruits. Barracks need steel and recruits, brought by rail: join them to home with track.</>,
      ],
    },
    {
      title: "Ground and people",
      lines: [
        <>Ground your units hold becomes yours, slowly; the map shades it in your colour, deeper the longer it's held.</>,
        <>Your settled ground sets how many people you can have, and so how big an army you can raise.</>,
      ],
    },
    {
      title: "Going deeper",
      lines: [
        <>
          {k("research")} Research: new materials and capabilities, paid for in goods and output.
        </>,
        <>{k("designs")} Designs: build your own units from a hull and components, titans included.</>,
        <>Infantry can be robots once Robotics is researched: they need no people, at a little more steel and some copper. Machines are repaired by repair crews, not healed by medics.</>,
        <>{k("market")} Market: trade goods with other players.</>,
      ],
    },
  ];
  return (
    <>
      <h1>How to play</h1>
      <div class="how-to">
        <For each={topics}>
          {(t) => (
            <section class="how-topic">
              <h2>{t.title}</h2>
              <ul>
                <For each={t.lines}>{(l) => <li>{l}</li>}</For>
              </ul>
            </section>
          )}
        </For>
      </div>
      <section class="how-structures">
        <h2>Structures</h2>
        <div class="structure-list">
          <For each={OTHER_STRUCTURES}>
            {(x) => (
              <div class="structure-row">
                <Show when={x.g && glyph(x.g)} fallback={<span class="structure-track" />}>
                  {(src) => <img class="structure-glyph" src={src()} alt="" />}
                </Show>
                <span class="structure-name">{x.name}</span>
                <span class="structure-steel">{x.steel && !x.steel.includes("steel") ? `${x.steel} steel` : x.steel}</span>
                <span class="structure-does">{x.does}</span>
              </div>
            )}
          </For>
        </div>
        <For each={families}>
          {(family) => (
            <>
              <h3>{family}</h3>
              <div class="structure-list">
                <For each={rows.filter((p) => p.family === family)}>
                  {(x) => (
                    <div class="structure-row">
                      <Show when={x.icon} fallback={<Show when={glyph(x.g)}>{(src) => <img class="structure-glyph" src={src()} alt="" />}</Show>}>
                        {(g) => <span class="structure-glyph" innerHTML={goodSvg(g(), 32)} />}
                      </Show>
                      <span class="structure-name">{x.name}</span>
                      <span class="structure-steel">{`${x.steel} steel`}</span>
                      <span class="structure-does">{x.does}</span>
                    </div>
                  )}
                </For>
              </div>
            </>
          )}
        </For>
        <h2>How goods flow</h2>
        <div class="chains">
          <For each={chains}>
            {(chain) => (
              <div class="chain">
                <For each={chain}>
                  {([step, makes], i) => (
                    <>
                      <Show when={i() > 0}>
                        <span class="chain-arrow" aria-hidden="true">
                          →
                        </span>
                      </Show>
                      <span class="chain-box">
                        {step}
                        <Show when={makes}>
                          <small>{makes}</small>
                        </Show>
                      </span>
                    </>
                  )}
                </For>
              </div>
            )}
          </For>
        </div>
        <ul class="chain-rules">
          <li>Goods on their way show as arrows along the rail (Layers, Goods flow).</li>
          <li>Trains come from your home and from locomotive works. A station with freight and no train of its own borrows one from a station that has two.</li>
          <li>Goods move by train between stations, along your track. A plant uses only what's at its own station.</li>
          <li>Building costs steel, but your first mines (4), and your first 2 stations, barracks, steelworks, munitions plants and recruiting offices, are free, and track is free until your steel production is running. The build menu says what's still free.</li>
        </ul>
      </section>
      <section class="how-structures">
        <h2>Reading the map</h2>
        <div class="map-lines">
          <For each={LINES}>
            {([sample, means]) => (
              <>
                <svg class="map-line" viewBox="0 0 48 16" width="48" height="16" aria-hidden="true">
                  {sample}
                </svg>
                <span>{means}</span>
              </>
            )}
          </For>
        </div>
      </section>
    </>
  );
}
